import { useEffect, useState } from "react";

import { AlertTriangle, Clock, Cloud, RefreshCw, Wifi, X } from "lucide-react";

const VisibilityErrorDialog = ({ onClose, onRetry, errorMessage }) => {
  const [activeTab, setActiveTab] = useState("details");
  // Lock body scroll while dialog is mounted
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  return (
    <div className="audit-loader-overlay">
      <div className="audit-loader-card max-w-lg border-red-500/30 bg-gradient-to-br from-[#1E1E2E] to-[#120f1f]">
        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-6 ring-2 ring-red-500/20">
          <AlertTriangle className="w-8 h-8 text-red-400" />
        </div>

        <h2 className="text-2xl font-bold text-white mb-2">Unable to Check AI Visibility</h2>

        <p className="text-gray-400 text-lg mb-6 max-w-md">
          We weren't able to get responses from our AI providers for your query. This is usually a
          temporary issue.
        </p>

        {/* Tabs */}
        <div className="flex w-full border-b border-white/10 mb-6">
          <button
            onClick={() => setActiveTab("details")}
            className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
              activeTab === "details"
                ? "text-orange-400 border-b-2 border-orange-400"
                : "text-gray-500 hover:text-gray-300"
            }`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab("causes")}
            className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
              activeTab === "causes"
                ? "text-orange-400 border-b-2 border-orange-400"
                : "text-gray-500 hover:text-gray-300"
            }`}
          >
            What Happened
          </button>
        </div>

        {activeTab === "details" && errorMessage && (
          <div className="bg-red-500/5 border border-red-500/10 rounded-xl px-5 py-3 w-full">
            <p className="text-red-300 text-sm break-words">{errorMessage}</p>
          </div>
        )}

        {activeTab === "causes" && (
          <div className="bg-orange-500/5 border border-orange-500/10 rounded-xl p-6 text-left w-full">
            <h3 className="text-orange-300 font-semibold mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> What might have happened
            </h3>
            <ul className="space-y-3 text-gray-400 text-sm">
              <li className="flex items-start gap-3">
                <Cloud className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                <span>
                  One or more AI providers (ChatGPT, Gemini, Claude) may be temporarily unavailable
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                <span>The request may have timed out due to high demand</span>
              </li>
              <li className="flex items-start gap-3">
                <Wifi className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                <span>There may be a temporary connection issue</span>
              </li>
            </ul>
          </div>
        )}

        <div className="flex gap-4 w-full mt-8">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-6 rounded-lg font-medium text-gray-400 hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" /> Close
          </button>
          <button
            onClick={onRetry}
            className="flex-1 py-3 px-6 rounded-lg font-bold text-white bg-orange-600 hover:bg-orange-500 transition-colors shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" /> Try Again
          </button>
        </div>
      </div>
    </div>
  );
};

export default VisibilityErrorDialog;
